<script setup>
import { useLayout } from '@/layout/composables/layout';
import { useUserStore } from '@/stores/userStore';
import { supabase } from '@/supabase';
import { getSignedImageUrl } from '@/utils/get-wasabi-url';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AppConfigurator from './AppConfigurator.vue';

const { t } = useI18n();
const router = useRouter();
const { toggleMenu, toggleDarkMode, isDarkTheme } = useLayout();
const menu = ref();

const userStore = useUserStore();

const { name, image: userImage, role: userRole, id: userId, firstName, lastName } = storeToRefs(userStore);

// =========================
//  LOGOUT
// =========================
const isParticipant = computed(() => {
    const role = (userRole.value || localStorage.getItem('role') || '').toLowerCase();
    return role === 'participant';
});
const logout = async () => {
    await supabase.auth.signOut();

    // optional cleanup
    localStorage.clear();
    sessionStorage.clear();

    userStore.clearUser();

    router.push('/');
};

// =========================
// =========================
const profile = () => router.push('/profile');

// =========================
//  DROPDOWN ITEMS
// =========================
const items = computed(() => [
    {
        items: [
            { label: t('profile'), icon: 'pi pi-cog', command: profile },
            // { label: t('logout'), icon: 'pi pi-sign-out', command: logout }
        ]
    }
]);

// =========================
//  WINDOW RESIZE
// =========================
const windowWidth = ref(window.innerWidth);
const handleResize = () => (windowWidth.value = window.innerWidth);

// =========================
//  MOUNT
// =========================
onMounted(async () => {
    window.addEventListener('resize', handleResize);

    const currentUserId = userId.value || localStorage.getItem('user_id');
    if (!currentUserId) return;

    //  If store has data → skip API
    if (userStore.name) {
        return;
    }

    //  Fetch only once
    await fetchUserData(currentUserId);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

// =========================
//  FETCH USER
// =========================
const fetchUserData = async (userId) => {
    try {
        const { data, error } = await supabase.from('users').select('first_name, last_name, profile_img, role').eq('id', userId).single();

        if (error || !data) return;

        const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ');

        //  Store update
        userStore.setUser({
            name: fullName,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            role: data.role || ''
        });

        //  Image
        if (data.profile_img) {
            const signedUrl = await getSignedImageUrl(data.profile_img);
            userStore.setImage(signedUrl);
        }
    } catch (err) {
        console.error('User fetch error:', err);
    }
};

// =========================
//  COMPUTED
// =========================
const role_display = computed(() => {
    const role = userRole.value || localStorage.getItem('role') || '';
    return role.charAt(0).toUpperCase() + role.slice(1);
});

const avatarLabel = computed(() => {
    const first = (firstName.value || '').charAt(0).toUpperCase();
    const last = (lastName.value || '').charAt(0).toUpperCase();
    return (first + last) || '?';
});

// =========================
//  MENU TOGGLE
// =========================
const toggle = (event) => {
    menu.value.toggle(event);
};
</script>

<template>
    <div class="layout-topbar">
        <!-- LEFT -->
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" @click="toggleMenu">
                <i class="pi pi-bars"></i>
            </button>

            <!-- <router-link to="/" class="layout-topbar-logo flex items-center gap-3">
                <img src="@/assets/images/digi_quiz_logo.png" class="h-12 md:h-14" />
            </router-link> -->
            <router-link v-if="!isParticipant" to="/" class="layout-topbar-logo flex items-center gap-3">
                <img src="@/assets/images/digi_quiz_logo.png" class="h-12 md:h-14" />
            </router-link>
            <div v-else class="layout-topbar-logo flex items-center gap-3">
                <img src="@/assets/images/digi_quiz_logo.png" class="h-12 md:h-14" />
            </div>
        </div>

        <!-- RIGHT -->
        <div class="layout-topbar-actions">
            <!-- THEME -->
            <button class="layout-topbar-action" @click="toggleDarkMode">
                <i :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]"></i>
            </button>

            <!-- CONFIG -->
            <div class="relative">
                <button
                    v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveToClass: 'hidden', leaveActiveClass: 'animate-fadeout', hideOnOutsideClick: true }"
                    class="layout-topbar-action layout-topbar-action-highlight"
                >
                    <i class="pi pi-palette"></i>
                </button>
                <AppConfigurator />
            </div>

            <!-- PROFILE BUTTON -->
            <button class="layout-topbar-action" @click="toggle">
                <i class="pi pi-user"></i>
                <span>Profile</span>
            </button>

            <!-- DROPDOWN -->
            <Menu ref="menu" :model="items" popup>
                <template #end>
                    <button class="flex items-center gap-2 p-2 w-full">
                        <!-- Avatar -->
                        <Avatar v-if="userImage" :image="userImage" shape="circle" />
                        <Avatar v-else :label="avatarLabel" class="bg-primary text-white" shape="circle" />

                        <!-- Name + Role -->
                        <div class="flex flex-col text-left">
                            <span class="font-bold">{{ (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (name || 'User') }}</span>
                            <span class="text-sm">{{ role_display }}</span>
                        </div>
                    </button>
                </template>
            </Menu>

            <!-- MOBILE LOGOUT -->
            <!-- <button class="layout-topbar-action lg:hidden" @click="logout">
                <i class="pi pi-sign-out"></i>
                <span>Logout</span>
            </button> -->
        </div>
    </div>
</template>

<style scoped>
.layout-topbar-logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>
